import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, Star, ImageIcon, FileText, TrendingUp, ArrowRight, Boxes, Tag, Plus, ShoppingCart, LineChart } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { adminAPI } from '../../services/api';

const StatCard = ({ icon: Icon, label, value, sub, color = 'blue' }) => (
  <Card data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 font-medium">{label}</p>
          <p className={`text-3xl font-bold text-${color}-600 mt-1`}>{value?.toLocaleString() ?? '—'}</p>
          {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
        </div>
        <div className={`p-3 rounded-lg bg-${color}-50`}>
          <Icon className={`h-6 w-6 text-${color}-600`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const DashboardHome = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminAPI.getStats()
      .then(setStats)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-center text-gray-500">Loading stats…</div>;
  if (!stats) return <div className="p-8 text-center text-red-600">Failed to load stats</div>;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Dashboard</h2>
        <p className="text-gray-600 text-sm mt-1">Overview of your BlueSpark Australia store</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Package} label="Products" value={stats.products} sub={`${stats.in_stock} in stock`} color="blue" />
        <StatCard icon={Boxes} label="Vehicle Makes" value={stats.makes} sub="across catalog" color="purple" />
        <StatCard icon={Tag} label="Categories" value={stats.categories} color="green" />
        <StatCard icon={Star} label="Reviews" value={stats.reviews} color="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center"><TrendingUp className="h-5 w-5 mr-2" /> Products by Category</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {stats.category_breakdown.map((row) => {
              const pct = stats.products > 0 ? (row.count / stats.products) * 100 : 0;
              return (
                <div key={row.category}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium">{row.category}</span>
                    <span className="text-gray-500">{row.count.toLocaleString()} ({pct.toFixed(1)}%)</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-600 rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Link to="/admin/dashboard/orders">
              <Button className="w-full justify-between bg-blue-600 hover:bg-blue-700 text-white" data-testid="quick-orders">
                <span className="flex items-center"><ShoppingCart className="mr-2 h-4 w-4" /> View Orders</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link to="/admin/dashboard/sales">
              <Button variant="outline" className="w-full justify-between" data-testid="quick-sales">
                <span className="flex items-center"><LineChart className="mr-2 h-4 w-4" /> Sales Analytics</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link to="/admin/dashboard/products?new=1">
              <Button variant="outline" className="w-full justify-between" data-testid="quick-add-product">
                <span className="flex items-center"><Plus className="mr-2 h-4 w-4" /> Add New Product</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link to="/admin/dashboard/products">
              <Button variant="outline" className="w-full justify-between" data-testid="quick-products">
                <span className="flex items-center"><Package className="mr-2 h-4 w-4" /> Manage Products</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link to="/admin/dashboard/bulk-upload">
              <Button variant="outline" className="w-full justify-between" data-testid="quick-prices">
                <span className="flex items-center"><Tag className="mr-2 h-4 w-4" /> Bulk Upload / Update Products</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link to="/admin/dashboard/carousel">
              <Button variant="outline" className="w-full justify-between" data-testid="quick-carousel">
                <span className="flex items-center"><ImageIcon className="mr-2 h-4 w-4" /> Edit Hero Carousel ({stats.carousel_slides})</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link to="/admin/dashboard/faq">
              <Button variant="outline" className="w-full justify-between" data-testid="quick-faq">
                <span className="flex items-center"><FileText className="mr-2 h-4 w-4" /> Edit FAQ ({stats.faqs} sections)</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link to="/admin/dashboard/reviews">
              <Button variant="outline" className="w-full justify-between" data-testid="quick-reviews">
                <span className="flex items-center"><Star className="mr-2 h-4 w-4" /> Manage Reviews ({stats.reviews})</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardHome;
